import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DialogContent, DialogTitle, Typography} from "@mui/material";
import useIsMounted from "../../../hooks/use-is-mounted";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import {CustomerGiftCardTypeIds} from "../../../../core/constants/enums";
import Utils, {dateComparator, numComparator, stringComparator} from "../../../../core/services/utils/utils";
import Api from "../../../../core/services/api/api";
import SearchInput from "../../base/input/search_input";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../packages/data-grid";
import PartnerDataGrid from "../../../containers/data-grid";
import TryAgain from "../../app-specific/try-again";
import GiftCard from "../../app-specific/payment-cards/gift-card";
import {AddGiftCardToOrderDataGridColumnNames} from "../../../../core/constants/table/data-grid-column-keys";


const dataGridSaveConfig = {
    name: 'AddGiftCardToOrderDataGrid',
    version: 1,
}

const AddGiftCardToOrderDialog = ({
                                      open,
                                      setOpen,
                                      onSuccess,
                                      orderId,
                                      customerId,
                                  }) => {
    const [data, setData] = useState([]);
    const [keywords, setKeywords] = useState('');
    const [redeeming, setRedeeming] = useState(false);
    const [loadingCards, setLoadingCards] = useState(false);
    const [loading, setLoading] = useState(undefined);
    const isMounted = useIsMounted();

    const refillableGiftCard = useMemo(() => data?.find(e => e.type?.id === CustomerGiftCardTypeIds.refillable), [data]);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Card Number",
                name: AddGiftCardToOrderDataGridColumnNames.cardNumber,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 140,
                pinned: true,
            },
            {
                title: "Category",
                name: AddGiftCardToOrderDataGridColumnNames.category,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Expiry Date",
                name: AddGiftCardToOrderDataGridColumnNames.expiryDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
            },
            {
                title: "Amount",
                name: AddGiftCardToOrderDataGridColumnNames.amount,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.number,
            },
            {
                name: AddGiftCardToOrderDataGridColumnNames.select,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
                pinnedToggleable: false,
            },
        ], [])


    const [dataGridColumns, dataGridSortBy, , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
        sortByObjectMap: {
            property: 'field',
            descending: 'descending',
        },
    });
    const [orderBy, setOrderBy] = useState(dataGridSortBy);


    /**@type {function(a: any, b: any): number}*/
    const sortByMethod = useMemo(() => {
        switch (orderBy?.field) {
            case AddGiftCardToOrderDataGridColumnNames.cardNumber:
                return (a, b) => orderBy.descending
                    ? stringComparator(a?.cardNumber ?? '', b?.cardNumber ?? '')
                    : stringComparator(b?.cardNumber ?? '', a?.cardNumber ?? '')
            case AddGiftCardToOrderDataGridColumnNames.category:
                return (a, b) => orderBy.descending
                    ? stringComparator(a?.category?.name ?? '', b?.category?.name ?? '')
                    : stringComparator(b?.category?.name ?? '', a?.category?.name ?? '')
            case AddGiftCardToOrderDataGridColumnNames.expiryDate:
                return (a, b) => orderBy.descending
                    ? dateComparator(a?.expiryDate ?? '', b?.expiryDate ?? '')
                    : dateComparator(b?.expiryDate ?? '', a?.expiryDate ?? '')
            case AddGiftCardToOrderDataGridColumnNames.amount:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.amount ?? 0, b?.amount ?? 0)
                    : numComparator(b?.amount ?? 0, a?.amount ?? 0)
            default:
                return () => () => 0
        }
    }, [orderBy])

    /**@type {any[]}*/
    const visibleGiftCards = useMemo(() => {
        return data?.filter(e => (
            refillableGiftCard?.cardNumber !== e.cardNumber
            && (
                !keywords?.length ||
                e?.cardNumber?.toLowerCase()?.includes(keywords?.toLowerCase())
            )
        )) ?? []
    }, [keywords, data, refillableGiftCard])

    /**
     * Fetches the gift cards from the server and if the result of the api is successful, sets the list
     */
    const getGiftCards = useCallback(async () => {
        setLoadingCards(true);
        const response = await Api.getCustomerGiftCards(customerId, false);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setData(response?.data);
        }
        setLoadingCards(false);
    }, [customerId, isMounted])

    /**
     * With each change in the [open] state of the dialog:
     * - fetches the available gift cards from the server
     */
    useEffect(() => {
        if (!open) {
            setKeywords('');
            return
        }
        void getGiftCards();
    }, [open, getGiftCards])


    /**
     * Sets the selected gift card for the specific order and then calls the onSuccess callback.
     * @param giftCard {any}
     */
    const selectGiftCard = useCallback(async (giftCard) => {
        setLoading(giftCard.cardNumber);
        const forApi = {
            customerId: customerId,
            giftCardNumber: giftCard.cardNumber,
            orderId: Number(orderId)
        }
        const response = await Api.addGiftCardToQuote(forApi)
        if (!isMounted())
            return;
        setLoading(undefined);
        if (response?.resultFlag) {
            setOpen(false);
            onSuccess(response.data);
        }
    }, [customerId, orderId, isMounted, setOpen, onSuccess])

    /**
     * Redeems the saved keywords as the new coupon to be given to the selected user.
     * - if the result of the api is successful, then adds this coupon to the list of gift cards
     */
    const redeemCoupon = async () => {
        setRedeeming(true);
        const forApi = {
            code: keywords,
            customerId: customerId,
            redeemAmount: null
        }
        const response = await Api.redeemCoupon(forApi);
        if (!isMounted())
            return;
        setRedeeming(false);
        if (response?.resultFlag) {
            // Coupon already redeemed
            if (data.map(e => e.id).includes(response?.data?.id)) {
                await Utils.wait(100);
                if (!isMounted())
                    return;
                setKeywords(response.data?.cardNumber ?? '')
                return;
            }
            setData(prevState => [
                {...response?.data, currentBalance: response?.data?.amount ?? 0},
                ...prevState
            ]);
            await Utils.wait(100);
            if (!isMounted())
                return;
            setKeywords(response.data?.cardNumber ?? '')
        }
    }

    /**
     * Sorts the entries in place.
     * @param {DataGridSortBy} tableOrderBy
     */
    const onSort = (tableOrderBy) => {
        setOrderBy(tableOrderBy);
    }


    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return Array.from(visibleGiftCards)
            ?.sort(sortByMethod)
            ?.map(card => ({
                key: card.cardNumber,
                data: card,
                cells: {
                    [AddGiftCardToOrderDataGridColumnNames.cardNumber]: card.cardNumber,
                    [AddGiftCardToOrderDataGridColumnNames.category]: card.category?.name,
                    [AddGiftCardToOrderDataGridColumnNames.expiryDate]: card.expiryDate,
                    [AddGiftCardToOrderDataGridColumnNames.amount]: card.amount,
                    [AddGiftCardToOrderDataGridColumnNames.select]:
                        <Button
                            disabled={loading}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => selectGiftCard(card)}
                        >
                            {
                                loading === card.cardNumber
                                    ? "processing..."
                                    : "Select"
                            }
                        </Button>,
                }
            }))
    }, [visibleGiftCards, sortByMethod, loading, selectGiftCard])

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            loading={!!loading}
            disableEscapeKeyDown={!!loading}
            disableBackdropClick={!!loading}
            maxWidth={'md'}
            className={'preview-dialog bound-dialog'}
        >
            <DialogTitle className={'p-3 px-5'} id="scroll-dialog-title">
                <p className={'text-lg text-color-1 font-weight-semi-bold'}>
                    Add Gift Card / Coupon
                </p>
            </DialogTitle>
            <DialogContent dividers className={'p-3 d-flex flex-column align-items-center w-100'}>
                {
                    refillableGiftCard &&
                    <div className={'mx-4 mb-4 mt-2 d-flex flex-column'}>
                        <Typography variant={'body1'} fontWeight={600} marginBlockEnd={2}>
                            Refillable Gift Card:
                        </Typography>
                        <GiftCard
                            card={refillableGiftCard}
                            buttonText={loading ? 'Processing...' : 'Select'}
                            buttonDisabled={!!loading}
                            onButtonPressed={() => selectGiftCard(refillableGiftCard)}
                            className={''}
                        />
                    </div>
                }
                <div className='my-3 w-100'/>
                <SearchInput
                    loading={loadingCards}
                    autoSearch={true}
                    search={(keywords) => setKeywords(keywords?.trim() ?? '')}
                    initialValue={keywords}
                    variant={'outlined'}
                    label={'Search Gift Card / Coupons'}
                    placeholder={'search gift cards or coupons...'}
                    paperProps={{
                        className: 'px-0',
                    }}
                />
                {(!!keywords?.trim()?.length && !visibleGiftCards.length && !loading)
                    ? <TryAgain
                        text={"There is no gift cards or coupons with the given filter. You can" +
                            " redeem them if you desire."}
                        showButton={true}
                        buttonText={redeeming ? "processing..." : "Redeem as a Coupon"}
                        onClick={() => !redeeming && redeemCoupon()}
                    />
                    : <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        hideFooter
                        hideExporting
                        columns={dataGridColumns}
                        rows={dataGridRows}
                        state={{
                            sortBy: orderBy
                                ? {field: orderBy.propertyName, descending: orderBy.isDescending}
                                : null,
                            loading: {state: loadingCards},
                            density: dataGridDensity,
                        }}
                        onSortByChanged={(_, current) => onSort(current)}
                        {...(
                            !!keywords?.trim()?.length && !data?.length
                                ? {
                                    EmptyContentComponent:
                                        <TryAgain
                                            text={"There is no gift cards or coupons with the given filter. You can" +
                                                " redeem them if you desire."}
                                            showButton={true}
                                            buttonText={redeeming ? "processing..." : "Redeem as a Coupon"}
                                            onClick={() => !redeeming && redeemCoupon()}
                                        />
                                }
                                : {}
                        )}
                    />
                }

            </DialogContent>
        </Dialog>
    );
}


export default AddGiftCardToOrderDialog;
